import { Box, Stack, Text } from '@chakra-ui/react'

import { SUMMER_CAMP_END_DATE, SUMMER_CAMP_YEAR } from '@/client/services/summerCamp'
import { getSATBootcampUrl } from '@/client/services/url'
import { getArticleUrl } from '@/client/services/url/faq'

import SignUpLink from '@/components/Auth/SignUpLink'
import FAQ from '@/components/FAQ/FAQ'
import ShwLink from '@/components/ShwLink'

const FAQ_ITEMS = [
  {
    question: 'What is Schoolhouse?',
    answer: () => (
      <Text>
        Schoolhouse is a free, peer-to-peer tutoring platform by Sal Khan, founder of Khan Academy.
        In addition to the Summer Camp program, Schoolhouse offers{' '}
        <ShwLink href={getSATBootcampUrl()}>free SAT Bootcamps</ShwLink>, live homework help, and
        more!
      </Text>
    ),
  },
  {
    question: 'Who can join Summer Camp?',
    answer: () => (
      <Text>
        Anyone across the world with access to the internet and Zoom! However, please note that you
        must be at least 13 years old to make an account on Schoolhouse.
      </Text>
    ),
  },
  {
    question: 'How do I participate in Summer Camp?',
    answer: () => (
      <Text>
        Participating in Schoolhouse Summer Camp is simple. First,{' '}
        <SignUpLink display="inline">make an account</SignUpLink> on Schoolhouse. After your
        application to join Schoolhouse is approved, you can register for any Summer Camp session
        with open spots!
      </Text>
    ),
  },
  {
    question: 'Is there any cost to attend Summer Camp?',
    answer: () => (
      <Text>
        Summer Camp (as well as tutoring sessions) at Schoolhouse is completely free. Our platform
        is powered by a community of passionate tutors, and our mission is to connect the world
        through learning via access to free educational opportunities.
      </Text>
    ),
  },
  {
    question: 'Who is hosting Summer Camp sessions?',
    answer: () => (
      <Text>
        Summer Camp sessions are planned and hosted by Camp Counselors—who come from our global
        community of volunteer peer tutors, many of whom are students themselves! Our tutors are
        passionate about sharing what they know, giving back to others, and building a community
        that celebrates the fun in learning.
      </Text>
    ),
  },
  {
    question: 'How can I host a Summer Camp session?',
    answer: () => (
      <Text>
        Summer Camp sessions are designed for a fun, engaging spin on educational topics. To host
        one, either create a new series in the Enrichment subworld, or if you want to teach in a
        different subworld, mark each topic in your series as Community Events. Your series will
        then automatically be included in summer camp, and you&apos;ll see a confirmation before you
        publish it.
      </Text>
    ),
  },
  {
    question: 'What is the required commitment?',
    answer: () => (
      <Text>
        Attending Schoolhouse Summer Camp is meant to be fun and relaxing. Choose to attend just one
        session or 50 sessions—it&apos;s up to you! However, spots are limited in sessions so please
        be mindful. If you can no longer attend a session you registered for, please withdraw from
        the session so another camper can have fun too!
      </Text>
    ),
  },
  {
    question: 'Do Summer Camp sessions grant volunteer hours?',
    answer: () => (
      <Text>
        Any sessions that normally grant volunteer hours will continue to be eligible for volunteer
        hours, even if they are a Summer Camp session. However, since Summer Camp sessions are not
        meant to be academic tutoring, they will generally not be eligible. To confirm if your
        session is eligible, check out{' '}
        <ShwLink href={getArticleUrl('volunteer-hour-policies-in-subworlds')}>
          the volunteer hours policy
        </ShwLink>{' '}
        that goes over which subworlds you can receive volunteer hours in.
      </Text>
    ),
  },
  {
    question: 'Are there any incentives associated with hosting or attending summer camp sessions?',
    answer: () => (
      <Stack direction="column" spacing="16px">
        <Text>
          Yes! We have different incentives for both tutors and learners. Tutors who host at least 3
          summer camp sessions will receive a Summer Camp {SUMMER_CAMP_YEAR} badge on their profile,
          and any tutors who have at least 50 unique learners attend their official sessions will
          receive <b>custom Summer Camp {SUMMER_CAMP_YEAR} merch!</b> Learners who attend at least 6
          Summer Camp sessions will receive a Summer Camp {SUMMER_CAMP_YEAR} badge on their profile
          and learners who attend summer camp sessions will be entered into a Summer Camp merch
          raffle! Each session you attend counts as a raffle ticket!
        </Text>
        <Text>
          Please note all Summer Camp incentives will be awarded by{' '}
          {SUMMER_CAMP_END_DATE.plus({ months: 1 }).toFormat('MMMM d')} and any communication
          related to earning or winning Summer Camp merch will happen through the email associated
          with your Schoolhouse account!
        </Text>
      </Stack>
    ),
  },
]

export default function SummerCampFAQ(): React.ReactElement {
  return (
    <Box maxWidth="800px" margin="auto" my="64px">
      <FAQ items={FAQ_ITEMS} />
    </Box>
  )
}
